/**
 * Batch Reporter - Helper for reporting progress in batch/long-running actions via SSE
 * @module core/sse/batch-reporter
 */

import type { SSESender, BatchProgressEvent, BatchLogEntry } from '@gzl10/nexus-sdk'

/**
 * Reporter interface for batch actions to emit structured progress events
 */
export interface BatchReporter {
  /** Signal that the batch process has started */
  start(message?: string): void
  /** Report progress with percentage and optional step info */
  progress(percent: number, message?: string, step?: number, totalSteps?: number): void
  /** Append a log entry (sent incrementally to client) */
  log(level: 'info' | 'warn' | 'error', message: string): void
  /** Signal successful completion with optional result data */
  complete(result?: unknown): void
  /** Signal failure with error code and message */
  fail(code: string, message: string): void
}

/**
 * Create a BatchReporter that emits structured progress events via SSE.
 * All events use the named event 'batch' so the client can listen with addEventListener('batch', ...).
 *
 * @param sender - SSE sender from ctx.core.sse.stream()
 * @returns BatchReporter instance
 *
 * @example
 * ```typescript
 * // In an action handler:
 * const reporter = input._batchReporter as BatchReporter
 * reporter.start('Importing records...')
 * for (let i = 0; i < records.length; i++) {
 *   await processRecord(records[i])
 *   reporter.progress(Math.round((i + 1) / records.length * 100), `Processed ${i + 1}/${records.length}`, i + 1, records.length)
 * }
 * reporter.complete({ imported: records.length })
 * ```
 */
export function createBatchReporter(sender: SSESender): BatchReporter {
  const logs: BatchLogEntry[] = []

  function send(event: BatchProgressEvent): void {
    sender.send(event, 'batch')
  }

  return {
    start(message?: string): void {
      send({ status: 'started', percent: 0, message })
    },

    progress(percent: number, message?: string, step?: number, totalSteps?: number): void {
      send({ status: 'progress', percent, message, step, totalSteps })
    },

    log(level: 'info' | 'warn' | 'error', message: string): void {
      const entry: BatchLogEntry = { level, message, timestamp: new Date().toISOString() }
      logs.push(entry)
      send({ status: 'progress', logs: [entry] })
    },

    complete(result?: unknown): void {
      send({ status: 'completed', percent: 100, result, logs })
      sender.close()
    },

    fail(code: string, message: string): void {
      send({ status: 'error', error: { code, message }, logs })
      sender.close()
    }
  }
}
